// Prodotto aggiuntivo: GAP Furto + RSA con satellitare.
//
// L'API `quoteSatellitare` risponde con la sola opzione quotata per il
// massimale e la durata del preventivo:
//
//   {
//     productCode: "GAP_RSA_SAT",
//     option: {
//       coverageLimit, durationMonths, gapPremium,
//       satelliteCost, satelliteSubscription, satelliteTotal, totaleCliente,
//       riepilogoCosti: { label, totalAmount, dataset: [{ label, value }] },
//       informativa:    { dataset: [{ label, value }] }
//     }
//   }
//
// Come si compone il costo (internamente):
//   impianto una tantum + (abbonamento annuo × anni) + premio GAP
// es. massimale 5.000 € su 24 mesi: 200 + (50 × 2) + 88 = 388 €
//
// Al cliente però si mostrano solo due voci: l'impianto e l'abbonamento
// aggregato al premio GAP ("Abbonamento con garanzia di ritrovamento").
//
// Le caratteristiche del prodotto (nome, massimali RSA, giorni di indennizzo,
// raggio km…) non dipendono dal preventivo e stanno qui sotto come costanti.
// Se l'API le manda comunque, i suoi valori hanno la precedenza.

export const DEFAULT_GAP_LIMITS = [1500, 3000, 5000];
export const DEFAULT_GAP_LIMIT = "3000";
export const DEFAULT_DURATIONS = [12, 24, 36, 48, 60];

export const DEFAULT_GAP_PRODUCT_CODE = "GAP_RSA_SAT";

// impianto una tantum e abbonamento annuo: fallback se l'API non li manda
export const SATELLITE_INSTALL_COST = 200;
export const SATELLITE_YEARLY_SUBSCRIPTION = 50;

// le uniche due voci mostrate al cliente
export const COST_LABEL_INSTALL = "Costo impianto iniziale";
export const COST_LABEL_SUBSCRIPTION = "Abbonamento con garanzia di ritrovamento";
export const COST_SUMMARY_LABEL = "Riepilogo costi";

// schede prodotto: valori fissi, non dipendono da massimale/durata/veicolo
export const GAP_PRODUCTS = {
  GAP_RSA_SAT: {
    productCode: "GAP_RSA_SAT",
    productName: "GAP Furto + RSA",
    description:
      "Proteggi il cliente anche in caso di mancato ritrovamento del veicolo, con assistenza dedicata e copertura abbinata al sistema satellitare.",
    requiresSatellite: true,
    indemnityAfterDays: 45,
    rsaLimit: 120,
    recoveryTripLimit: 300,
    recoveryTripKm: 50,
  },
};

// scheda del prodotto + eventuali campi mandati dall'API (che vincono)
export const getGapProductInfo = (satellitare) => {
  const code = satellitare?.productCode || DEFAULT_GAP_PRODUCT_CODE;
  const info = GAP_PRODUCTS[code] || GAP_PRODUCTS[DEFAULT_GAP_PRODUCT_CODE];
  return { ...info, ...(satellitare || {}) };
};

const currency = (value) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value ?? 0);

const toNumber = (value) => {
  // attenzione: Number(null) è 0, quindi null/undefined/"" vanno scartati prima
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// anni di abbonamento: l'impianto si paga una volta, l'abbonamento per anno
const yearsFromMonths = (months) =>
  months ? Math.max(1, Math.ceil(months / 12)) : 1;

// dataset dell'API già pronto per la vista: solo label + valore numerico
const normalizeDataset = (dataset) =>
  (dataset ?? [])
    .map((row) => ({ label: row?.label, value: toNumber(row?.value) }))
    .filter((row) => row.label && row.value !== null);

const normalizeOption = (option) => {
  if (!option) return null;
  // l'API manda gapPremium null quando il massimale non ha tariffa: in quel
  // caso l'opzione non è quotabile e non va mostrata con un prezzo qualsiasi
  const gapPremium = toNumber(option.gapPremium);
  if (gapPremium === null) return null;

  const durationMonths = toNumber(option.durationMonths);
  const years = yearsFromMonths(durationMonths);

  const satelliteCost = toNumber(option.satelliteCost) ?? SATELLITE_INSTALL_COST;
  const satelliteSubscription =
    toNumber(option.satelliteSubscription) ??
    years * SATELLITE_YEARLY_SUBSCRIPTION;
  const satelliteTotal =
    toNumber(option.satelliteTotal) ?? satelliteCost + satelliteSubscription;

  // voce unica mostrata al cliente: abbonamento satellitare + premio GAP
  const subscriptionWithGap = satelliteSubscription + gapPremium;

  const apiRows = normalizeDataset(option.riepilogoCosti?.dataset);
  const costRows = apiRows.length
    ? apiRows
    : [
        { label: COST_LABEL_INSTALL, value: satelliteCost },
        { label: COST_LABEL_SUBSCRIPTION, value: subscriptionWithGap },
      ];

  const totaleCliente =
    toNumber(option.totaleCliente) ??
    toNumber(option.riepilogoCosti?.totalAmount) ??
    satelliteCost + subscriptionWithGap;

  return {
    coverageLimit: toNumber(option.coverageLimit),
    durationMonths,
    years,
    gapPremium,
    satelliteCost,
    satelliteSubscription,
    satelliteTotal,
    subscriptionWithGap,
    totaleCliente,
    costSummaryLabel: option.riepilogoCosti?.label || COST_SUMMARY_LABEL,
    costRows,
    informativa: option.informativa?.dataset ?? null,
  };
};

const matchesDuration = (option, months) => {
  const optionMonths = toNumber(option.durationMonths);
  return optionMonths === null || months === null || optionMonths === months;
};

// l'API può rispondere con la sola opzione richiesta (`option`) oppure con
// l'elenco delle opzioni disponibili (`options`)
const readOptions = (satellitare) => {
  if (satellitare?.option) return [satellitare.option];
  return satellitare?.options ?? [];
};

// riga quotata per il massimale e la durata del preventivo
export const findSatelliteOption = (satellitare, coverageLimit, months) => {
  const rows = readOptions(satellitare);
  const limit = toNumber(coverageLimit);
  const duration = toNumber(months);

  const option = rows.find(
    (row) =>
      toNumber(row.coverageLimit) === limit && matchesDuration(row, duration)
  );
  // se la function ha già quotato il solo massimale richiesto, quella è la riga
  if (!option && rows.length === 1) return normalizeOption(rows[0]);

  return normalizeOption(option);
};

// tutte le righe quotate per la durata del preventivo, per massimale crescente
export const getSatelliteOptions = (satellitare, months) => {
  const duration = toNumber(months);
  return readOptions(satellitare)
    .filter((row) => matchesDuration(row, duration))
    .map(normalizeOption)
    .filter(Boolean)
    .sort((a, b) => (a.coverageLimit ?? 0) - (b.coverageLimit ?? 0));
};

export const getAvailableLimits = (satellitare) => {
  if (satellitare?.availableLimits?.length) return satellitare.availableLimits;
  const fromOptions = [
    ...new Set(
      readOptions(satellitare).map((row) => toNumber(row.coverageLimit))
    ),
  ].filter((value) => value !== null);
  return fromOptions.length
    ? fromOptions.sort((a, b) => a - b)
    : DEFAULT_GAP_LIMITS;
};

// scheda informativa: quella dell'API se c'è, altrimenti dalle costanti
export const getInformativa = (option, product) => {
  if (option?.informativa?.length) return option.informativa;

  const rows = [
    {
      label: "Indennizzo dopo",
      value: `${product?.indemnityAfterDays ?? "-"} giorni`,
    },
    { label: "Massimale RSA", value: currency(product?.rsaLimit) },
    { label: "Recupero veicolo", value: currency(product?.recoveryTripLimit) },
    { label: "Raggio Km", value: `${product?.recoveryTripKm ?? "-"} Km` },
  ];

  if (option?.coverageLimit) {
    rows.push({
      label: "Massimale GAP",
      value: `${currency(option.coverageLimit)} dal preventivo`,
    });
  }
  if (option?.durationMonths) {
    rows.push({
      label: "Durata",
      value: `${option.durationMonths} mesi dalla CVT`,
    });
  }
  return rows;
};

// selezione salvata nel preventivo: i dettagli servono al riepilogo emissione
export const buildSatelliteSelection = (product, option) => {
  if (!option) return null;
  return {
    product: {
      productCode: product?.productCode,
      productName: product?.productName,
    },
    selectedLimit: option.coverageLimit,
    selectedDuration: option.durationMonths,
    // dettaglio interno: non si mostra al cliente
    premium: option.gapPremium,
    satelliteInitial: option.satelliteCost,
    satelliteSubscription: option.satelliteSubscription,
    // voci mostrate al cliente
    subscriptionWithGap: option.subscriptionWithGap,
    costRows: option.costRows,
    total: option.totaleCliente,
  };
};

// Dati statici di ripiego: il JSON dei preventivi di rcAutoDetails non manda
// ancora l'oggetto `satellitare`. Stessa forma della risposta di
// `quoteSatellitare`, con l'esempio documentato sopra:
// massimale 5.000 € su 24 mesi → 200 + (50 × 2) + 88 = 388 €.
// Da rimuovere quando l'API comincia a quotare il prodotto.
export const MOCK_SATELLITARE = {
  productCode: DEFAULT_GAP_PRODUCT_CODE,
  option: {
    coverageLimit: 5000,
    durationMonths: 24,
    gapPremium: 88,
    satelliteCost: SATELLITE_INSTALL_COST,
    satelliteSubscription: 2 * SATELLITE_YEARLY_SUBSCRIPTION,
  },
};
